import { Fragment, useState, useEffect } from 'react'
import {
  EuiAccordion,
  EuiButton,
  EuiSpacer,
  EuiHorizontalRule,
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiFieldPassword,
  EuiCallOut,
  EuiLoadingSpinner,
  EuiFlexGroup,
  EuiFlexItem,
} from '@elastic/eui'
import settingsService from '../services/settings.service'

/**
 * Formulaire structuré de configuration runtime (table cv_settings).
 *
 * Sécurité front :
 *  - les secrets ne sont jamais reçus en clair (le back renvoie { hasValue }) ;
 *  - ils sont saisis en write-only (EuiFieldPassword) ; un champ laissé vide
 *    conserve la valeur existante côté serveur.
 *  - la sauvegarde déclenche un hot-reload côté back (pas de redémarrage).
 */
function AdminSettingsForm() {
  const [schema, setSchema] = useState([])
  const [fields, setFields] = useState({})        // valeurs non-secrètes : { "section.key": "..." }
  const [secrets, setSecrets] = useState({})      // saisies secrètes : { "section.key": "..." }
  const [hasValue, setHasValue] = useState({})    // état secret existant : { "section.key": bool }
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [status, setStatus] = useState(null)      // { type: 'success'|'danger', text }

  const load = () => {
    setIsLoading(true)
    settingsService
      .getSettings()
      .then((res) => {
        const { schema: formSchema, values } = res.data || {}
        setSchema(formSchema || [])
        const f = {}
        const hv = {}
        ;(formSchema || []).forEach((s) => {
          s.fields.forEach((field) => {
            const id = `${s.section}.${field.key}`
            const current = values?.[s.section]?.[field.key]
            if (field.secret) {
              hv[id] = !!(current && current.hasValue)
            } else {
              f[id] = current == null ? '' : String(current)
            }
          })
        })
        setFields(f)
        setHasValue(hv)
        setSecrets({})
        setDirty(false)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error('getSettings error =>', err)
        setStatus({ type: 'danger', text: 'Impossible de charger la configuration.' })
        setIsLoading(false)
      })
  }

  useEffect(load, [])

  const onChangeField = (id, value) => {
    setFields((prev) => ({ ...prev, [id]: value }))
    setDirty(true)
    setStatus(null)
  }

  const onChangeSecret = (id, value) => {
    setSecrets((prev) => ({ ...prev, [id]: value }))
    setDirty(true)
    setStatus(null)
  }

  const save = () => {
    setIsSaving(true)
    setStatus(null)

    // Construit { section: { key: value } } : non-secrets toujours, secrets seulement si saisis.
    const values = {}
    schema.forEach((s) => {
      values[s.section] = values[s.section] || {}
      s.fields.forEach((field) => {
        const id = `${s.section}.${field.key}`
        if (field.secret) {
          const typed = secrets[id]
          if (typed !== undefined && typed !== '') {
            values[s.section][field.key] = typed
          }
        } else {
          values[s.section][field.key] = fields[id] ?? ''
        }
      })
    })

    settingsService
      .postSettings(values)
      .then(() => {
        setStatus({ type: 'success', text: 'Configuration enregistrée et rechargée.' })
        load() // recharge l'état masqué (les secrets saisis repassent en write-only)
      })
      .catch((err) => {
        console.error('postSettings error =>', err)
        setStatus({ type: 'danger', text: "Échec de l'enregistrement de la configuration." })
        setIsSaving(false)
      })
      .finally(() => setIsSaving(false))
  }

  if (isLoading) {
    return (
      <Fragment>
        <EuiSpacer size="l" />
        <EuiFlexGroup alignItems="center" gutterSize="s">
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="m" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>Chargement de la configuration…</EuiFlexItem>
        </EuiFlexGroup>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <EuiSpacer size="xs" />
      {status && (
        <Fragment>
          <EuiCallOut
            size="s"
            color={status.type}
            title={status.text}
            iconType={status.type === 'success' ? 'check' : 'alert'}
          />
          <EuiSpacer size="s" />
        </Fragment>
      )}

      <EuiFlexGroup justifyContent="flexEnd" gutterSize="s">
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            color="warning"
            iconType="returnKey"
            isDisabled={!dirty || isSaving}
            onClick={load}
          >
            Annuler
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            color="primary"
            fill
            iconType="save"
            isDisabled={!dirty}
            isLoading={isSaving}
            onClick={save}
          >
            Enregistrer
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiHorizontalRule style={{ height: 2 }} />

      <EuiForm component="form" onSubmit={(e) => e.preventDefault()}>
        {schema.map((s, i) => (
          <Fragment key={s.section}>
            <EuiAccordion id={`section-${s.section}`} buttonContent={s.label} initialIsOpen={i === 0}>
              <EuiSpacer size="s" />
              {s.fields.map((field) => {
                const id = `${s.section}.${field.key}`
                return (
                  <EuiFormRow key={id} label={field.label} fullWidth>
                    {field.secret ? (
                      <EuiFieldPassword
                        type="dual"
                        fullWidth
                        value={secrets[id] || ''}
                        placeholder={
                          hasValue[id]
                            ? '●●●●●●●● — laisser vide pour conserver'
                            : 'non défini'
                        }
                        onChange={(e) => onChangeSecret(id, e.target.value)}
                      />
                    ) : (
                      <EuiFieldText
                        fullWidth
                        value={fields[id] || ''}
                        onChange={(e) => onChangeField(id, e.target.value)}
                      />
                    )}
                  </EuiFormRow>
                )
              })}
              <EuiSpacer size="m" />
            </EuiAccordion>
            <EuiHorizontalRule margin="s" />
          </Fragment>
        ))}
      </EuiForm>
    </Fragment>
  )
}

export default AdminSettingsForm
